import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FontIcon } from './FontIcon';
import type { Pdf as Interface, FileMetadata } from '../../design/components';
import { isFileMetadata } from '../shared/image';
import { isValidPdfSource, downloadPdf, type PdfDocProxy } from '../shared/pdf';

export const Pdf: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'No PDF selected',
  hidden,
  inline,
  readonly: _readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['' as unknown as FileMetadata, () => {}];
  const valueRef = useRef(value);
  valueRef.current = value;
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValid = isValidPdfSource(value);

  const displayUrl = useMemo(() => isValid ? (value as FileMetadata).url : '', [value, isValid]);

  useEffect(() => {
    return () => { if (displayUrl.startsWith('blob:')) URL.revokeObjectURL(displayUrl); };
  }, [displayUrl]);

  const handleDocumentLoadSuccess = (pdf: PdfDocProxy) => {
    setNumPages(pdf.numPages);
    const current = valueRef.current;
    if (isFileMetadata(current) && current.file) {
      (async () => {
        const text: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text.push(content.items.map(item => ('str' in item ? item.str : '')).join(' '));
        }
        const searchable = text.some(t => t.length > 0);
        setValue({ ...valueRef.current as FileMetadata, pages: pdf.numPages, searchable, text });
      })().catch(() => {});
    }
  };

  const handleDownload = () => {
    if (isFileMetadata(value)) downloadPdf(value.url, value.name);
  };

  const processFile = (file: File) => {
    if (file.type === 'application/pdf') {
      setValue({
        url: URL.createObjectURL(file),
        name: file.name, type: file.type, size: file.size,
        modified: new Date(file.lastModified).toISOString(),
        pages: 0, file,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 2);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isValid]);

  const controlsHeight = 40;
  const previewHeight = 300;
  const containerHeight = previewHeight + controlsHeight;

  if (inline) {
    return value ? (
      <Box sx={{ display: hidden ? 'none' : 'inline-block' }}>
        <FontIcon name="Description" size="small" />
      </Box>
    ) : null;
  }

  const canUpload = !_readonly && !disabled;

  return (
    <Box sx={{ display: hidden ? 'none' : undefined, mb: 2 }}>
      {name && (
        <Typography
          component="label"
          variant="body2"
          sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
        >
          {name}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      )}

      {/* PDF Preview */}
      {isValid && (
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            overflow: expanded ? 'auto' : 'hidden',
            height: expanded ? undefined : containerHeight,
            maxHeight: expanded ? '80vh' : undefined,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {/* Controls */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              backdropFilter: 'blur(8px)',
            }}
          >
            {numPages && numPages > 1 && (
              <>
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  <FontIcon name="ChevronLeft" size="small" />
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                  {currentPage} / {numPages}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage >= numPages}
                >
                  <FontIcon name="ChevronRight" size="small" />
                </IconButton>
              </>
            )}

            <Tooltip title={expanded ? 'Shrink' : 'Expand'}>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
              >
                <FontIcon name={expanded ? 'FullscreenExit' : 'Fullscreen'} size="small" />
              </IconButton>
            </Tooltip>

            {isFileMetadata(value) && (
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  component="a"
                  href={value.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontIcon name="OpenInNew" size="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={handleDownload}
              >
                <FontIcon name="Download" size="small" />
              </IconButton>
            </Tooltip>

            {canUpload && (
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => setValue('' as unknown as FileMetadata)}
                >
                  <FontIcon name="Delete" size="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Document
              file={displayUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={<Typography sx={{ p: 2 }} variant="body2" color="text.secondary">Loading...</Typography>}
              error={<Typography sx={{ p: 2 }} variant="body2" color="error">Failed to load PDF</Typography>}
              noData={<Typography sx={{ p: 2 }} variant="body2" color="text.secondary">No PDF</Typography>}
            >
              <Page
                pageNumber={currentPage}
                width={expanded ? containerWidth : undefined}
                height={expanded ? undefined : previewHeight}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={<Typography sx={{ p: 2 }} variant="body2" color="text.secondary">Loading page...</Typography>}
                error={<Typography sx={{ p: 2 }} variant="body2" color="error">Failed to load page</Typography>}
              />
            </Document>
          </Box>
        </Box>
      )}

      {/* Upload zone - empty state */}
      {!isValid && canUpload && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 96,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: dragOver ? 'primary.main' : error ? 'error.main' : 'divider',
            bgcolor: dragOver ? 'primary.50' : 'action.hover',
            color: 'text.secondary',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <FontIcon name="Upload" />
          <Typography variant="body2" sx={{ mt: 0.5 }}>Click or drag to upload PDF</Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>
      )}

      {/* Static empty state for readonly/disabled */}
      {!isValid && !canUpload && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 96,
            borderRadius: 1,
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            bgcolor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <FontIcon name="Description" />
          <Typography sx={{ ml: 1 }}>{placeholder}</Typography>
        </Box>
      )}

      {(error || description) && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: error ? 'error.main' : 'text.secondary' }}
        >
          {error || description}
        </Typography>
      )}
    </Box>
  );
};
